import { useCallback, useEffect, useRef, useState } from 'react';
import useIsServiceInstalled from './useIsServiceInstalled';
import useMarketplaceFetch from './useMarketplaceFetch';
import { useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import { useDebouncedCallback } from 'use-debounce';
import { ValidPlatformArch } from '@kemu-io/hs-types';
import useMissingHubServices from './useMissingHubServices';
import { MarketplaceRecordWithRatings } from '@kemu-io/kemu-types';
import { useHubContext } from '@common/context/HubContext/HubContext';


export type MissingServiceInfo = {
	/** The name of the service */
  marketplaceItemId: string;
	name: string;
	/** The required version of the service */
	version: string;
	/** The display name of the service */
	displayName?: string;
	/** The description of the service */
	description?: string;
	/** Whether the service is installed but with a lower version */
	installedWithLowerVersion: boolean;
	/** Whether the service is installed but with missing secrets */
	installedWithMissingSecrets: boolean;
	/** Whether the service is not installed at all */
	// notInstalled: boolean;
	/** The author of the service */
	author: string;
  svgIcon: string;
  shortTitle?: string;
  isKemuOfficial: boolean;
  platform: ValidPlatformArch;
};

/**
 * Hook to detect missing services from the current recipe.
 * @returns an object containing the list of missing services and loading state
 */
const useMissingHubServicesInfo = () => {
  const [missingServices, setMissingServices] = useState<MissingServiceInfo[]>([]);
  const logicMapperContext = useLogicMapperContext();
	const { isReady } = useHubContext();
	const getIsInstalled = useIsServiceInstalled();
  const searchPublications = useMarketplaceFetch();
  const servicesNotFound = useMissingHubServices();
  const previouslyFetchedNamesRef = useRef<{names: string, results: MarketplaceRecordWithRatings[]} | null>(null);
  const platform = logicMapperContext.systemInfo?.platformArch;

  const fetchPublicationsInfo = useCallback(async (names: string[]) => {
    if(!platform) {
      return [];
    }

    const response = await searchPublications({ names, platform, from: 0, total: names.length });
    return response;
  }, [searchPublications, platform]);

  /**
   * Determines which services are missing or have issues
   */
  const determineMissingServices = useDebouncedCallback(async () => {
    if (!isReady || servicesNotFound.length === 0) {
      setMissingServices([]);
      return;
    }

    const missing: MissingServiceInfo[] = [];
  
    const requiredServicesNames = servicesNotFound.map(s => s.name);
    const requiredServicesNamesString = requiredServicesNames.join(',');
    
    let requiredServicesInfo: MarketplaceRecordWithRatings[] = previouslyFetchedNamesRef.current?.results || [];

    // Only fetch if the names have changed
    if(requiredServicesNamesString !== previouslyFetchedNamesRef.current?.names) {
      requiredServicesInfo = await fetchPublicationsInfo(requiredServicesNames).catch(() => []);
      if(!requiredServicesInfo) {
        setMissingServices([]);
        return;
      };

      // Cache the results
      previouslyFetchedNamesRef.current = { 
        names: requiredServicesNamesString, 
        results: requiredServicesInfo
      };
    }
    

    for (const requiredService of servicesNotFound) {
      const servicesInfo = requiredServicesInfo.find(s => s.name === requiredService.name);
  
      // Check if service is missing or has issues
      if (servicesInfo) {
        missing.push({
          marketplaceItemId: servicesInfo.id,
          name: requiredService.name,
          version: requiredService.version,
          displayName: servicesInfo?.title || requiredService.name,
          description: servicesInfo?.description,
          author: servicesInfo.author.organizationName,
          svgIcon: servicesInfo.iconSvg,
          shortTitle: servicesInfo.shortTitle,
          installedWithLowerVersion: requiredService.installedWithLowerVersion,
          installedWithMissingSecrets: requiredService.installedWithMissingSecrets || false,
          isKemuOfficial: servicesInfo.isKemuOfficial || false,
          platform: servicesInfo.platform,
        });
      }
    }
  
    setMissingServices(missing);
  }, 100);

	/**
	 * Determines which services are missing or have issues
	 */
	useEffect(() => {
		if (!isReady) {
			return;
		}

    determineMissingServices();

    return () => {
      determineMissingServices.cancel();
    };
	}, [servicesNotFound, isReady, getIsInstalled]);

	return {
		missingServices,
		isLoading: !isReady,
		hasMissingServices: missingServices.length > 0,
	};
};

export default useMissingHubServicesInfo;
